import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

import usePartner from '@/hooks/usePartner';
import { PartnerProps } from '@/models/Partner';
import { PartnerGroupProps } from '@/models/PartnerGroup';
import { ProgramNames } from '@/models/Program';

import { UsersContext } from './UsersContext';
import { PartnerGroupSelectModel } from '@/models/PartnerGroupSelectModel';
import { logError } from '@/utils/errorHandlerUtil';

interface Props {
  partnerId: number | null;
  partnerGroupId: number | null;
  partnerInfo: PartnerProps | null;
  partnerGroupInfo: PartnerGroupProps | null;
  partnersAll: Record<ProgramNames, PartnerProps[]>;
  partnerGroupSelectOptions: PartnerGroupSelectModel[];
  partnersInPartnerGroup?: PartnerProps[] | null;
  updatePartnerId: (newPartnerId: number | null) => void;
  updatePartnerGroupId: (partnerGroupId: number | null) => void;
  updatePartnerInfo: (info: PartnerProps) => void;
  updatePartnerGroupInfo: (info: PartnerGroupProps | null) => void;
  updatePartnersAll: (partnerData: Record<ProgramNames, PartnerProps[]>) => void;
  updatePartnersInPartnerGroup: React.Dispatch<React.SetStateAction<PartnerProps[] | null>>;
}

interface ProviderProps {
  children: React.ReactNode;
  partnerId?: number | null;
}

export const SelectedPartnerContext = createContext<Props>({
  partnerId: null,
  partnerGroupId: null,
  partnerInfo: null,
  partnerGroupInfo: null,
  partnersAll: {} as Record<ProgramNames, PartnerProps[]>,
  partnerGroupSelectOptions: [],
  updatePartnerId: () => {},
  updatePartnerGroupId: () => {},
  updatePartnerInfo: () => {},
  updatePartnerGroupInfo: () => {},
  updatePartnersAll: () => {},
  updatePartnersInPartnerGroup: () => {},
});

export const SelectedPartnerProvider = ({
  children,
  partnerId: initialPartnerId,
}: ProviderProps) => {
  const { getPartner, getPartnerGroup, getPartnerGroupSelectOptions, getPartnersByPartnerGroupId } =
    usePartner();
  const { userCurrent } = useContext(UsersContext);

  const [partnerId, setPartnerId] = useState<number | null>(
    initialPartnerId ?? userCurrent.currentPartnerId ?? null
  );
  const [partnerGroupId, setPartnerGroupId] = useState<number | null>(null);
  const [partnerInfo, setPartnerInfo] = useState<PartnerProps | null>(null);
  const [partnersInPartnerGroup, setPartnersInPartnerGroup] = useState<PartnerProps[] | null>(null);
  const [partnerGroupInfo, setPartnerGroupInfo] = useState<PartnerGroupProps | null>(null);

  const [partnerGroupSelectOptions, setPartnerGroupSelectOptions] = useState<
    PartnerGroupSelectModel[]
  >([]);

  const [partnersAll, setPartnersAll] = useState<Record<ProgramNames, PartnerProps[]>>(
    {} as Record<ProgramNames, PartnerProps[]>
  );

  const updatePartnerId = useCallback((newPartnerId: number | null) => {
    setPartnerId((prevPartnerId) => {
      if (prevPartnerId !== newPartnerId) {
        return newPartnerId;
      }
      return prevPartnerId;
    });
  }, []);

  const updatePartnerGroupInfo = useCallback((info: PartnerGroupProps | null) => {
    setPartnerGroupInfo(info);
  }, []);

  const updatePartnerInfo = useCallback((info: PartnerProps) => {
    setPartnerInfo(info);
  }, []);

  // TODO(bug): if the API returns an empty list, every response is a fresh [] identity, so this
  // effect re-runs and refetches in an unbounded loop (~1000 requests/100ms in tests); a null
  // (204/empty body) response throws on `.length`, and a rejection is unhandled. Expected: guard
  // with a hasFetched ref (like ConfigContext) and catch errors.
  useEffect(() => {
    const fetchPartnerGroupSelectOptions = async () => {
      const res = await getPartnerGroupSelectOptions();
      setPartnerGroupSelectOptions(res);
    };
    if (partnerGroupSelectOptions.length === 0) {
      void fetchPartnerGroupSelectOptions();
    }
  }, [getPartnerGroupSelectOptions, partnerGroupSelectOptions]);

  useEffect(() => {
    setPartnersInPartnerGroup(null);
  }, [partnerGroupId]);

  useEffect(() => {
    const fetchPartnerDetails = async (localPartnerId: number) => {
      const part = await getPartner(localPartnerId);
      setPartnerInfo(part);
    };

    if (partnerId == null) {
      setPartnerInfo(null);
      setPartnersInPartnerGroup(null);
    } else if (partnerId != null && (partnerInfo == null || partnerId != partnerInfo.id)) {
      void fetchPartnerDetails(partnerId);
    }
  }, [partnerId, partnerInfo, getPartner]);

  const updatePartnersInPartnerGroup = useCallback(
    async (updatedPartnerGroupId: number | null) => {
      if (updatedPartnerGroupId == null) {
        setPartnersInPartnerGroup(null);
        return;
      }

      const partners = await getPartnersByPartnerGroupId(updatedPartnerGroupId);
      setPartnersInPartnerGroup(partners);
    },
    [getPartnersByPartnerGroupId]
  );

  const updatePartnerGroupId = useCallback(
    async (updatedPartnerGroupId: number | null) => {
      setPartnerGroupId((prevPartnerGroupId) => {
        if (prevPartnerGroupId !== updatedPartnerGroupId) {
          return updatedPartnerGroupId;
        }
        return prevPartnerGroupId;
      });

      await updatePartnersInPartnerGroup(updatedPartnerGroupId); // pass -1 or any invalid ID to fetch function to indicate no group selected
    },
    [updatePartnersInPartnerGroup]
  );

  useEffect(() => {
    if (partnerGroupId == null) {
      if (partnerGroupInfo !== null) setPartnerGroupInfo(null);
      return;
    }

    // already have the correct group loaded
    if (partnerGroupInfo?.id === partnerGroupId) return;

    let cancelled = false;

    getPartnerGroup(partnerGroupId)
      .then((pg: PartnerGroupProps) => {
        if (cancelled) return;

        // only set if actually different
        setPartnerGroupInfo((prev) => (prev?.id === pg.id ? prev : pg));
      })
      .catch((error: unknown) => {
        logError(`Error fetching partner group info for ID: ${partnerGroupId}.`, error);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partnerGroupId, partnerGroupInfo?.id, getPartnerGroup]);

  useEffect(() => {
    if (
      userCurrent != null &&
      userCurrent.partnerGroup?.id != null &&
      userCurrent.partnerGroup.id != partnerGroupId
    ) {
      void updatePartnerGroupId(userCurrent.partnerGroup.id);
    }
  }, [userCurrent, partnerGroupId, updatePartnerGroupId]);

  const updatePartnersAll = useCallback((partnerData: Record<ProgramNames, PartnerProps[]>) => {
    setPartnersAll(partnerData);
  }, []);

  return (
    <SelectedPartnerContext.Provider
      value={{
        partnerId,
        partnerGroupId,
        partnerInfo,
        partnerGroupInfo,
        partnersAll,
        partnersInPartnerGroup,
        partnerGroupSelectOptions,
        updatePartnerId,
        updatePartnerGroupId,
        updatePartnerInfo,
        updatePartnerGroupInfo,
        updatePartnersAll,
        updatePartnersInPartnerGroup: setPartnersInPartnerGroup,
      }}
    >
      {children}
    </SelectedPartnerContext.Provider>
  );
};
